import React from 'react';
import { MARK_LINK } from 'storyblok-rich-text-react-renderer';
import { ErrorMessage, Field } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import EnterIcon from '../../img/PFF-brand-icons-thin-enter.svg';
import {
  bodyLinkStyles,
  bodySRegularBrownStyles,
  bodyXxsRegularBrownStyles,
  bodyXxxsRegularBrownStyles,
  eyebrowMStyles,
  eyebrowSStyles,
} from '../../styles/typography';
import { bpWidth, colors } from '../../styles/variables';
import { renderRichTextReact } from '../../utils/storyblokRichText';

const FormBodyWrapper = styled.div`
  &.hide {
    display: none;
  }
`;

const InputSubmit = styled.div`
  border-bottom: 1px solid ${colors.gray100};
  cursor: pointer;
  display: flex;
`;

const Input = styled(Field).attrs({
  type: 'email',
})`
  ${eyebrowMStyles};
  background: transparent;
  border: none;

  width: 100%;

  &::placeholder {
    color: ${colors.navy600};
  }
  &::selection {
    background: pink;
  }

  @media (${bpWidth.desktopSm}) {
    ${eyebrowSStyles}
  }
`;

const Submit = styled.button.attrs({
  type: 'submit',
})`
  background: none;
  border: none;

  padding: 0;

  svg {
    height: 21px;
  }

  svg,
  svg * {
    color: ${colors.navy600};
    fill: ${colors.navy600};
  }

  @media (${bpWidth.desktopSm}) {
    svg {
      height: 19px;
    }
  }
`;

const Disclaimer = styled.div`
  ${bodyXxsRegularBrownStyles};
  color: ${colors.gray300};
  padding-top: 10px;

  @media (${bpWidth.desktopSm}) {
    ${bodyXxxsRegularBrownStyles};
    color: ${colors.gray300};
  }
`;

const ExternalLink = styled.a.attrs({
  target: '_blank',
})`
  ${bodyLinkStyles};
  color: ${colors.cerulean400} !important;

  &:hover {
    color: ${colors.cerulean700} !important;
  }
`;

const ErrorStyles = styled.div`
  ${bodySRegularBrownStyles};
  color: ${colors.orange700};

  @media (${bpWidth.desktopSm}) {
    ${bodyXxsRegularBrownStyles};
    color: ${colors.orange700};
  }
`;

/**
 * @type {RenderOptions}
 */
const renderOptions = {
  markResolvers: {
    [MARK_LINK]: (children, { href }) => <ExternalLink href={href}>{children}</ExternalLink>,
  },
};

/**
 * Compact newsletter form used in the site footer and hamburger menu. `privacyPolicyText` is
 * the CMS-managed disclaimer; call sites without a Storyblok footer blok (the legacy footer
 * and menu) fall back to the hardcoded copy below, which is what they rendered before.
 */
function FooterEmailForm({ onFormSubmit, privacyPolicyText }) {
  return (
    <form onSubmit={onFormSubmit}>
      <FormBodyWrapper>
        <InputSubmit>
          <Input name="email" placeholder="NEWSLETTER SIGN UP" />

          <Submit>
            <EnterIcon alt="Form enter icon" />
          </Submit>
        </InputSubmit>

        <ErrorStyles>
          <ErrorMessage name="email" />
        </ErrorStyles>

        {privacyPolicyText ? (
          <Disclaimer>{renderRichTextReact(privacyPolicyText, renderOptions)}</Disclaimer>
        ) : (
          <Disclaimer>
            By signing up, you agree to Piaggio Fast Forward&apos;s{' '}
            <ExternalLink href="/privacy-policy">Privacy Policy</ExternalLink> and{' '}
            <ExternalLink href="/terms-of-use">Terms of Use</ExternalLink>.
          </Disclaimer>
        )}
      </FormBodyWrapper>
    </form>
  );
}

FooterEmailForm.propTypes = {
  onFormSubmit: PropTypes.func.isRequired,
  privacyPolicyText: PropTypes.object,
};

export default FooterEmailForm;
